/**
 * BOW Products – Header & Mega Menu JavaScript
 * Handles: Desktop mega menu, tab switching, accordion, sticky header, mobile sliding drawer
 */

(function () {
  "use strict";

  /* =========================================
     DESKTOP MEGA MENU
     ========================================= */
  class BowDesktopMegaMenu {
    constructor(header) {
      this.header = header;
      this.trigger = header.querySelector("[data-mega-trigger]");
      this.megaMenu = header.querySelector("[data-mega-menu]");
      if (!this.trigger || !this.megaMenu) return;

      this.tabs = this.megaMenu.querySelectorAll(".bow-mm__tab:not(.bow-mm__tab--direct)");
      this.directTabs = this.megaMenu.querySelectorAll(".bow-mm__tab--direct");
      this.panels = this.megaMenu.querySelectorAll(".bow-mm__panel");
      this.groupToggles = this.megaMenu.querySelectorAll(
        ".bow-mm__group-toggle",
      );

      this.isOpen = false;
      this.init();
    }

    init() {
      // Hover open (desktop)
      this.trigger.addEventListener("mouseenter", () => this.open());
      this.header.addEventListener("mouseleave", (e) => {
        if (!this.header.contains(e.relatedTarget)) this.close();
      });

      // Click toggle (all devices)
      this.trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.isOpen ? this.close() : this.open();
      });

      // Tab switching
      this.tabs.forEach((tab) => {
        tab.addEventListener("mouseenter", () => this.activateTab(tab));
        tab.addEventListener("click", () => this.activateTab(tab));
      });

      // Direct-link tabs: hovering clears any active panel (Option B)
      this.directTabs.forEach((tab) => {
        tab.addEventListener("mouseenter", () => this.clearActive());
      });

      // Group accordion toggles
      this.groupToggles.forEach((toggle) => {
        toggle.addEventListener("click", () => this.toggleGroup(toggle));
      });

      // Close on outside click
      document.addEventListener("click", (e) => {
        if (this.isOpen && !this.header.contains(e.target)) this.close();
      });

      // Escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.isOpen) {
          this.close();
          this.trigger.focus();
        }
      });
    }

    open() {
      this.isOpen = true;
      this.megaMenu.classList.add('is-open');
      this.megaMenu.setAttribute('aria-hidden', 'false');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.isOpen = false;
      this.megaMenu.classList.remove('is-open');
      this.megaMenu.setAttribute('aria-hidden', 'true');
      this.trigger.setAttribute('aria-expanded', 'false');

      // Reset tab + panel state so next open starts fresh
      this.tabs.forEach((tab) => {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
      });
      this.panels.forEach((panel) => {
        panel.classList.remove('is-active');
      });
    }

    activateTab(activeTab) {
      const index = activeTab.dataset.tabIndex;

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      this.panels.forEach((panel) => {
        panel.classList.toggle("is-active", panel.dataset.panelIndex === index);
      });
    }

    clearActive() {
      this.tabs.forEach((tab) => {
        tab.classList.remove("is-active");
        tab.setAttribute("aria-selected", "false");
      });
      this.panels.forEach((panel) => {
        panel.classList.remove("is-active");
      });
    }

    toggleGroup(toggle) {
      const expanded = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", !expanded);

      const subItems = toggle.nextElementSibling;
      if (subItems) {
        subItems.classList.toggle("is-expanded", !expanded);
      }
    }
  }

  /* =========================================
     MOBILE SLIDING DRAWER NAV
     ========================================= */
  class BowMobileNav {
    constructor() {
      this.nav = document.getElementById("bow-mobile-nav");
      if (!this.nav) return;

      this.drawer = this.nav.querySelector(".bow-mobile-nav__drawer");
      this.panels = this.nav.querySelectorAll(".bow-mob-panel");
      this.panelStack = ["main"];

      this.init();
    }

    init() {
      // Open/close drawer via hamburger
      this.hamburger = document.querySelector("[data-hamburger]");
      if (this.hamburger) {
        this.hamburger.addEventListener("click", () => {
          this.nav.classList.contains("is-open")
            ? this.closeDrawer()
            : this.openDrawer();
        });
      }

      // Overlay click to close
      const overlay = this.nav.querySelector("[data-mobile-overlay]");
      if (overlay) {
        overlay.addEventListener("click", () => this.closeDrawer());
      }

      // Panel drill-in buttons
      this.nav.querySelectorAll("[data-panel-target]").forEach((btn) => {
        btn.addEventListener("click", () => {
          this.showPanel(btn.dataset.panelTarget);
        });
      });

      // Back buttons
      this.nav.querySelectorAll("[data-panel-back]").forEach((btn) => {
        btn.addEventListener("click", () => {
          this.goBack();
        });
      });

      // Group accordion toggles (in-panel)
      this.nav.querySelectorAll(".bow-mob-group__toggle").forEach((toggle) => {
        toggle.addEventListener("click", () => {
          const expanded = toggle.getAttribute("aria-expanded") === "true";
          toggle.setAttribute("aria-expanded", !expanded);
          const sub = toggle.nextElementSibling;
          if (sub) sub.classList.toggle("is-open", !expanded);
        });
      });

      // Escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.nav.classList.contains("is-open")) {
          this.closeDrawer();
        }
      });
    }

    openDrawer() {
      this.nav.classList.add("is-open");
      this.nav.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      if (this.hamburger) this.hamburger.setAttribute("aria-expanded", "true");
      // Reset to main panel
      this.panelStack = ["main"];
      this.renderPanels();
    }

    closeDrawer() {
      this.nav.classList.remove("is-open");
      this.nav.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      if (this.hamburger) this.hamburger.setAttribute("aria-expanded", "false");
      // Reset panels after transition
      setTimeout(() => {
        this.panelStack = ["main"];
        this.renderPanels();
      }, 300);
    }

    showPanel(targetId) {
      this.panelStack.push(targetId);
      this.renderPanels();
    }

    goBack() {
      // "Back to Main Menu" always resets to main
      this.panelStack = ["main"];
      this.renderPanels();
    }

    renderPanels() {
      const currentId = this.panelStack[this.panelStack.length - 1];
      const prevId =
        this.panelStack.length > 1
          ? this.panelStack[this.panelStack.length - 2]
          : null;

      this.panels.forEach((panel) => {
        const id = panel.dataset.panel;
        panel.classList.remove("is-active", "is-prev");

        if (id === currentId) {
          panel.classList.add("is-active");
        } else if (id === prevId) {
          panel.classList.add("is-prev");
        }
      });
    }
  }

  /* =========================================
     STICKY HEADER
     ========================================= */
  class BowStickyHeader {
    constructor(header) {
      this.header = header;
      if (!this.header) return;

      this.announcementBar = document.querySelector(".bow-announcement-bar");
      this.threshold = this.announcementBar
        ? this.announcementBar.offsetHeight
        : 0;

      let ticking = false;
      window.addEventListener(
        "scroll",
        () => {
          if (!ticking) {
            requestAnimationFrame(() => {
              this.header.classList.toggle(
                "is-sticky",
                window.scrollY > this.threshold,
              );
              ticking = false;
            });
            ticking = true;
          }
        },
        { passive: true },
      );
    }
  }

  /* =========================================
     INIT
     ========================================= */
  function init() {
    const header = document.getElementById("bow-header");
    if (!header) return;

    new BowDesktopMegaMenu(header);
    new BowMobileNav();
    new BowStickyHeader(header);
  }

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
